import type { V2 } from '../core/math';
import type { CityLayout } from '../world/types';

/**
 * The route to the objective (docs/UI.md "Route arrows"): the shortest way along the road graph from
 * where the player stands to the street nearest the target — the left-hand lane in a car (one-way
 * streets only the right way), the left pavement on foot (any way) — and where to paint the chevrons
 * on it. Pure and allocation-free after construction: the planner keeps its own scratch arrays and
 * writes into a `RoutePath` the caller owns, so the view can replan every second without garbage.
 */

/** A planned route: a polyline on the ground with the distance along it at each point. */
export interface RoutePath {
  xs: Float32Array;
  zs: Float32Array;
  /** Metres from the start to point i. */
  cum: Float32Array;
  count: number;
  /** Total length (m). */
  length: number;
  /** Walking route (chevrons on the pavement) rather than driving (on the lane). */
  walk: boolean;
}

/** Turning round on the spot costs this much (m) in a car: the route starts the way the bonnet points unless that's far worse. */
export const UTURN_PENALTY = 90;
/** On foot the last stretch goes from the kerb to the target itself when it is this close to the street (m). */
const WALK_LAST_LEG = 30;

export function makeRoutePath(capacity: number): RoutePath {
  return { xs: new Float32Array(capacity), zs: new Float32Array(capacity), cum: new Float32Array(capacity), count: 0, length: 0, walk: false };
}

/** The road graph prepared for repeated shortest-path queries (Dijkstra over ~100 junctions). */
export class RoutePlanner {
  private readonly n: number;
  private readonly dist: Float64Array;
  private readonly prevNode: Int32Array;
  private readonly prevEdge: Int32Array;
  private readonly prevFwd: Uint8Array;
  private readonly done: Uint8Array;
  /** Junction box radius per node (0 = a bend): the lane line is trimmed by part of it at turns. */
  private readonly trim: Float32Array;
  /** Adjacency: for node i, entries adjStart[i]..adjStart[i+1]-1 of adjEdge / adjFwd. */
  private readonly adjStart: Int32Array;
  private readonly adjEdge: Int32Array;
  private readonly adjFwd: Uint8Array;
  private readonly lens: Float32Array;
  /** Legs of the last plan (edge, forward, from t, to t along the travel direction). */
  private readonly legEdge: Int32Array;
  private readonly legFwd: Uint8Array;
  private readonly legT0: Float32Array;
  private readonly legT1: Float32Array;
  /** Most points a path can have: two per leg, plus the off-street end. */
  readonly capacity: number;

  constructor(private readonly layout: CityLayout) {
    const nodes = layout.roads.nodes;
    const edges = layout.roads.edges;
    this.n = nodes.length;
    this.dist = new Float64Array(this.n);
    this.prevNode = new Int32Array(this.n);
    this.prevEdge = new Int32Array(this.n);
    this.prevFwd = new Uint8Array(this.n);
    this.done = new Uint8Array(this.n);
    this.trim = new Float32Array(this.n);
    for (const it of layout.intersections) if (it.nodeId >= 0 && it.nodeId < this.n) this.trim[it.nodeId] = it.radius;
    this.lens = new Float32Array(edges.length);
    const deg = new Int32Array(this.n + 1);
    for (const e of edges) {
      const a = nodes[e.from]!.pos;
      const b = nodes[e.to]!.pos;
      this.lens[e.id] = Math.hypot(b.x - a.x, b.z - a.z);
      deg[e.from]!++;
      deg[e.to]!++;
    }
    this.adjStart = new Int32Array(this.n + 1);
    for (let i = 0; i < this.n; i++) this.adjStart[i + 1] = this.adjStart[i]! + deg[i]!;
    this.adjEdge = new Int32Array(this.adjStart[this.n]!);
    this.adjFwd = new Uint8Array(this.adjStart[this.n]!);
    const fill = new Int32Array(this.n);
    for (const e of edges) {
      let k = this.adjStart[e.from]! + fill[e.from]!++;
      this.adjEdge[k] = e.id;
      this.adjFwd[k] = 1;
      k = this.adjStart[e.to]! + fill[e.to]!++;
      this.adjEdge[k] = e.id;
      this.adjFwd[k] = 0;
    }
    this.legEdge = new Int32Array(this.n + 2);
    this.legFwd = new Uint8Array(this.n + 2);
    this.legT0 = new Float32Array(this.n + 2);
    this.legT1 = new Float32Array(this.n + 2);
    this.capacity = (this.n + 2) * 2 + 2;
  }

  /** May this edge be travelled that way? On foot always; in a car only with lanes that way. */
  private canGo(edgeId: number, fwd: boolean, walk: boolean): boolean {
    if (walk) return true;
    const e = this.layout.roads.edges[edgeId]!;
    return fwd ? e.lanesForward > 0 : !e.oneWay && e.lanesBackward > 0;
  }

  /** Does the straight line a → b cross a building's footprint (the last metre, the door itself, excepted)? */
  throughBuilding(ax: number, az: number, bx: number, bz: number): boolean {
    const len = Math.hypot(bx - ax, bz - az);
    const minX = Math.min(ax, bx) - 1;
    const maxX = Math.max(ax, bx) + 1;
    const minZ = Math.min(az, bz) - 1;
    const maxZ = Math.max(az, bz) + 1;
    for (const b of this.layout.buildings ?? []) {
      const r = Math.max(b.halfX, b.halfZ) * 1.42;
      if (b.center.x + r < minX || b.center.x - r > maxX || b.center.z + r < minZ || b.center.z - r > maxZ) continue;
      const c = Math.cos(b.angle);
      const sn = Math.sin(b.angle);
      for (let d = 0; d <= len - 1; d += 0.5) {
        const dx = ax + ((bx - ax) * d) / len - b.center.x;
        const dz = az + ((bz - az) * d) / len - b.center.z;
        if (Math.abs(dx * c - dz * sn) < b.halfX - 0.2 && Math.abs(dx * sn + dz * c) < b.halfZ - 0.2) return true;
      }
    }
    return false;
  }

  /** Nearest edge centre line to p: edge id and t (0..1 from `from`), or -1. No allocation. */
  private nearest(p: V2, out: { edge: number; t: number }): void {
    const nodes = this.layout.roads.nodes;
    let best = Infinity;
    out.edge = -1;
    out.t = 0;
    for (const e of this.layout.roads.edges) {
      const a = nodes[e.from]!.pos;
      const b = nodes[e.to]!.pos;
      const abx = b.x - a.x;
      const abz = b.z - a.z;
      const l2 = abx * abx + abz * abz || 1;
      let t = ((p.x - a.x) * abx + (p.z - a.z) * abz) / l2;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const dx = p.x - (a.x + abx * t);
      const dz = p.z - (a.z + abz * t);
      const d = dx * dx + dz * dz;
      if (d < best) {
        best = d;
        out.edge = e.id;
        out.t = t;
      }
    }
  }

  private readonly startHit = { edge: -1, t: 0 };
  private readonly goalHit = { edge: -1, t: 0 };

  /**
   * Plan from `from` to `to` into `out`. `heading` (a car's, radians, 0 = +Z) makes a route that starts
   * behind the bonnet cost a U-turn. False when there is no way (the path is then empty).
   */
  plan(from: V2, to: V2, walk: boolean, heading: number | null, out: RoutePath): boolean {
    out.count = 0;
    out.length = 0;
    out.walk = walk;
    const edges = this.layout.roads.edges;
    const nodes = this.layout.roads.nodes;
    if (!edges.length || out.xs.length < this.capacity) return false;
    const s = this.startHit;
    const g = this.goalHit;
    this.nearest(from, s);
    this.nearest(to, g);
    if (s.edge < 0 || g.edge < 0) return false;
    const se = edges[s.edge]!;
    const sLen = this.lens[s.edge]!;
    const gLen = this.lens[g.edge]!;
    const behind = (fwd: boolean): number => {
      if (walk || heading === null) return 0;
      const a = nodes[se.from]!.pos;
      const b = nodes[se.to]!.pos;
      const dx = (b.x - a.x) * (fwd ? 1 : -1);
      const dz = (b.z - a.z) * (fwd ? 1 : -1);
      return dx * Math.sin(heading) + dz * Math.cos(heading) < 0 ? UTURN_PENALTY : 0;
    };
    // Dijkstra from the start edge's two ends (reached by driving along it from the player's spot).
    this.dist.fill(Infinity);
    this.done.fill(0);
    this.prevNode.fill(-1);
    if (this.canGo(s.edge, true, walk)) this.dist[se.to] = Math.min(this.dist[se.to]!, (1 - s.t) * sLen + behind(true));
    if (this.canGo(s.edge, false, walk)) this.dist[se.from] = Math.min(this.dist[se.from]!, s.t * sLen + behind(false));
    for (;;) {
      let cur = -1;
      let cd = Infinity;
      for (let i = 0; i < this.n; i++) {
        if (!this.done[i] && this.dist[i]! < cd) {
          cd = this.dist[i]!;
          cur = i;
        }
      }
      if (cur < 0) break;
      this.done[cur] = 1;
      for (let k = this.adjStart[cur]!; k < this.adjStart[cur + 1]!; k++) {
        const eid = this.adjEdge[k]!;
        const fwd = this.adjFwd[k] === 1;
        if (!this.canGo(eid, fwd, walk)) continue;
        const e = edges[eid]!;
        const next = fwd ? e.to : e.from;
        // Alleys are a short cut on foot, a squeeze in a car.
        const c = cd + this.lens[eid]! * (e.alley && !walk ? 1.4 : 1);
        if (c < this.dist[next]!) {
          this.dist[next] = c;
          this.prevNode[next] = cur;
          this.prevEdge[next] = eid;
          this.prevFwd[next] = fwd ? 1 : 0;
        }
      }
    }
    // Arrive along the goal edge from either end, the way it may be travelled.
    const ge = edges[g.edge]!;
    let bestCost = Infinity;
    let endNode = -1;
    let endFwd = true;
    if (this.canGo(g.edge, true, walk) && this.dist[ge.from]! + g.t * gLen < bestCost) {
      bestCost = this.dist[ge.from]! + g.t * gLen;
      endNode = ge.from;
      endFwd = true;
    }
    if (this.canGo(g.edge, false, walk) && this.dist[ge.to]! + (1 - g.t) * gLen < bestCost) {
      bestCost = this.dist[ge.to]! + (1 - g.t) * gLen;
      endNode = ge.to;
      endFwd = false;
    }
    // Same street, the target ahead the way we may go: straight along it.
    let direct = false;
    if (s.edge === g.edge) {
      const fwd = g.t >= s.t;
      if (this.canGo(s.edge, fwd, walk)) {
        const c = Math.abs(g.t - s.t) * sLen + behind(fwd);
        if (c <= bestCost) {
          bestCost = c;
          direct = true;
          endFwd = fwd;
        }
      }
    }
    if (!Number.isFinite(bestCost)) return false;

    // Legs, start to goal. Travel-direction t: 0 = where the leg begins.
    let legs = 0;
    const push = (edge: number, fwd: boolean, t0: number, t1: number): void => {
      this.legEdge[legs] = edge;
      this.legFwd[legs] = fwd ? 1 : 0;
      this.legT0[legs] = t0;
      this.legT1[legs] = t1;
      legs++;
    };
    if (direct) {
      push(s.edge, endFwd, endFwd ? s.t : 1 - s.t, endFwd ? g.t : 1 - g.t);
    } else {
      // Walk back from the end node to whichever start end it came from.
      let count = 0;
      for (let v = endNode; this.prevNode[v]! >= 0 && count < this.n; v = this.prevNode[v]!) count++;
      const first = (() => {
        let v = endNode;
        for (let i = 0; i < count; i++) v = this.prevNode[v]!;
        return v;
      })();
      // The first leg: along the start edge from the player to `first`.
      const startFwd = first === se.to;
      push(s.edge, startFwd, startFwd ? s.t : 1 - s.t, 1);
      // Middle legs in order: fill backwards into their slots.
      const base = legs;
      legs += count;
      let v = endNode;
      for (let i = count - 1; i >= 0; i--) {
        this.legEdge[base + i] = this.prevEdge[v]!;
        this.legFwd[base + i] = this.prevFwd[v]!;
        this.legT0[base + i] = 0;
        this.legT1[base + i] = 1;
        v = this.prevNode[v]!;
      }
      push(g.edge, endFwd, 0, endFwd ? g.t : 1 - g.t);
    }

    // Points: each leg's lane (or pavement) line, trimmed into the junction boxes at the turns.
    let n = 0;
    let lastX = NaN;
    let lastZ = NaN;
    const add = (x: number, z: number): void => {
      if (n > 0 && Math.abs(x - lastX) < 0.05 && Math.abs(z - lastZ) < 0.05) return;
      out.xs[n] = x;
      out.zs[n] = z;
      out.cum[n] = n === 0 ? 0 : out.cum[n - 1]! + Math.hypot(x - lastX, z - lastZ);
      lastX = x;
      lastZ = z;
      n++;
    };
    for (let i = 0; i < legs; i++) {
      const e = edges[this.legEdge[i]!]!;
      const fwd = this.legFwd[i] === 1;
      const len = this.lens[e.id]!;
      if (len < 1e-3) continue;
      const a = nodes[fwd ? e.from : e.to]!;
      const b = nodes[fwd ? e.to : e.from]!;
      const dx = (b.pos.x - a.pos.x) / len;
      const dz = (b.pos.z - a.pos.z) / len;
      // Left of the travel direction (traffic keeps left): (dz, -dx).
      const off = walk
        ? e.alley || e.sidewalk <= 0
          ? 0
          : e.width / 2 + Math.max(0.6, e.sidewalk / 2)
        : e.oneWay || e.alley || e.width < 7
          ? 0
          : e.laneWidth * 0.5;
      let t0 = this.legT0[i]!;
      let t1 = this.legT1[i]!;
      // Trim into the junction at a turn (not at the player's or the target's end).
      if (i > 0 && t0 <= 0) t0 = Math.min(0.4, t1, (this.trim[a.id]! * 0.55) / len);
      if (i < legs - 1 && t1 >= 1) t1 = Math.max(t0, 1 - Math.min(0.4, (this.trim[b.id]! * 0.55) / len));
      add(a.pos.x + dx * len * t0 + dz * off, a.pos.z + dz * len * t0 - dx * off);
      add(a.pos.x + dx * len * t1 + dz * off, a.pos.z + dz * len * t1 - dx * off);
    }
    // On foot, the last few steps off the kerb to the door itself — never through a building (a door set
    // into the front wall, or a place behind the block, keeps the chevrons on the pavement).
    if (walk && n > 0 && Math.hypot(to.x - lastX, to.z - lastZ) < WALK_LAST_LEG && !this.throughBuilding(lastX, lastZ, to.x, to.z)) add(to.x, to.z);
    out.count = n;
    out.length = n > 0 ? out.cum[n - 1]! : 0;
    return n > 1;
  }
}

/** Distance along `path` of the point on it nearest to p, and how far p is from it. Allocation-free (writes `out`). */
export function projectOnPath(path: RoutePath, p: V2, out: { along: number; off: number }): void {
  out.along = 0;
  out.off = Infinity;
  for (let i = 0; i + 1 < path.count; i++) {
    const ax = path.xs[i]!;
    const az = path.zs[i]!;
    const bx = path.xs[i + 1]!;
    const bz = path.zs[i + 1]!;
    const abx = bx - ax;
    const abz = bz - az;
    const l2 = abx * abx + abz * abz;
    let t = l2 > 0 ? ((p.x - ax) * abx + (p.z - az) * abz) / l2 : 0;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const d = Math.hypot(p.x - (ax + abx * t), p.z - (az + abz * t));
    if (d < out.off) {
      out.off = d;
      out.along = path.cum[i]! + Math.sqrt(l2) * t;
    }
  }
  if (path.count === 1) {
    out.off = Math.hypot(p.x - path.xs[0]!, p.z - path.zs[0]!);
    out.along = 0;
  }
}

export interface ChevronOptions {
  /** Metres between chevrons. */
  spacing: number;
  /** How far ahead of the player they are painted (m). */
  ahead: number;
  /** None this close in front of the player (under the car / the feet). */
  skip: number;
  /** None in the last few metres before the target (the beacon is there). */
  endGap: number;
}

export const DRIVE_CHEVRONS: ChevronOptions = { spacing: 8, ahead: 200, skip: 7, endGap: 6 };
export const WALK_CHEVRONS: ChevronOptions = { spacing: 3.2, ahead: 85, skip: 2.5, endGap: 2.5 };

/**
 * Chevrons from `along` (the player's place on the path) up to `ahead` metres on, into `out` as
 * [x, z, heading, fade] quadruples; returns how many (never more than `max`). They sit at fixed
 * distances counted back from the target, so they stay put on the road as the player moves and as the
 * route is replanned; the furthest quarter of the window fades out.
 */
export function placeChevrons(path: RoutePath, along: number, o: ChevronOptions, out: Float32Array, max: number): number {
  const L = path.length;
  if (path.count < 2 || L <= 0) return 0;
  const near = along + o.skip;
  const far = Math.min(along + o.ahead, L - o.endGap);
  if (far <= near) return 0;
  const kmax = Math.floor((L - near) / o.spacing);
  const kmin = Math.ceil((L - far) / o.spacing);
  const cap = Math.min(max, Math.floor(out.length / 4));
  let count = 0;
  let seg = 0;
  for (let k = kmax; k >= kmin && count < cap; k--) {
    const sAt = L - k * o.spacing;
    while (seg + 2 < path.count && path.cum[seg + 1]! < sAt) seg++;
    const ax = path.xs[seg]!;
    const az = path.zs[seg]!;
    const bx = path.xs[seg + 1]!;
    const bz = path.zs[seg + 1]!;
    const segLen = path.cum[seg + 1]! - path.cum[seg]!;
    const t = segLen > 0 ? Math.min(1, Math.max(0, (sAt - path.cum[seg]!) / segLen)) : 0;
    const j = count * 4;
    out[j] = ax + (bx - ax) * t;
    out[j + 1] = az + (bz - az) * t;
    out[j + 2] = Math.atan2(bx - ax, bz - az);
    const fadeLen = o.ahead * 0.25;
    out[j + 3] = Math.min(1, Math.max(0, (along + o.ahead - sAt) / fadeLen));
    count++;
  }
  return count;
}
